import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";

const ManageOfferFieldsDialog = ({ open, setOpen, offers, fields, onApply }) => {
  const [sourceIndex, setSourceIndex] = useState(0);
  const [selectedFieldKeys, setSelectedFieldKeys] = useState([]);
  const [targetIndexes, setTargetIndexes] = useState([]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const defaultSource = 0;
    setSourceIndex(defaultSource);
    setSelectedFieldKeys([]);
    setTargetIndexes(
      offers
        .map((_, idx) => idx)
        .filter((idx) => idx !== defaultSource),
    );
  }, [open, offers]);

  const targetCandidates = useMemo(
    () => offers.map((offer, idx) => ({ offer, idx })).filter(({ idx }) => idx !== sourceIndex),
    [offers, sourceIndex],
  );

  const allFieldsSelected = fields.length > 0 && selectedFieldKeys.length === fields.length;

  const isSaveDisabled = selectedFieldKeys.length === 0 || targetIndexes.length === 0;

  const getOfferLabel = (offer, idx) => {
    const itemLabel = offer?.itemId || offer?.item?.id || "N/A";
    const offerDate = offer?.offerDate || "N/A";
    return `Offer ${idx + 1} (Item: ${itemLabel}, Date: ${offerDate})`;
  };

  const toggleField = (fieldKey) => {
    setSelectedFieldKeys((prev) =>
      prev.includes(fieldKey)
        ? prev.filter((key) => key !== fieldKey)
        : [...prev, fieldKey],
    );
  };

  const toggleTarget = (targetIndex) => {
    setTargetIndexes((prev) =>
      prev.includes(targetIndex)
        ? prev.filter((idx) => idx !== targetIndex)
        : [...prev, targetIndex],
    );
  };

  const handleSelectAllFields = (checked) => {
    if (checked) {
      setSelectedFieldKeys(fields.map((field) => field.key));
      return;
    }
    setSelectedFieldKeys([]);
  };

  const handleApply = () => {
    onApply({ sourceIndex, targetIndexes, selectedFieldKeys });
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>Manage Shared Fields</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2">
            Select one source offer, choose fields, then choose the target offers.
          </Typography>

          <Divider />

          <Box>
            <Typography variant="subtitle2" mb={1}>
              Source Offer
            </Typography>
            <RadioGroup
              value={String(sourceIndex)}
              onChange={(e) => {
                const nextSource = Number(e.target.value);
                setSourceIndex(nextSource);
                setTargetIndexes((prev) => prev.filter((idx) => idx !== nextSource));
              }}
            >
              {offers.map((offer, idx) => (
                <FormControlLabel
                  key={idx}
                  value={String(idx)}
                  control={<Radio size="small" />}
                  label={getOfferLabel(offer, idx)}
                />
              ))}
            </RadioGroup>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" mb={1}>
              Fields To Apply
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allFieldsSelected}
                  indeterminate={
                    selectedFieldKeys.length > 0 && selectedFieldKeys.length < fields.length
                  }
                  onChange={(e) => handleSelectAllFields(e.target.checked)}
                />
              }
              label="Select All"
            />
            <FormGroup>
              {fields.map((field) => (
                <FormControlLabel
                  key={field.key}
                  control={
                    <Checkbox
                      checked={selectedFieldKeys.includes(field.key)}
                      onChange={() => toggleField(field.key)}
                    />
                  }
                  label={field.label}
                />
              ))}
            </FormGroup>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" mb={1}>
              Target Offers
            </Typography>
            <FormGroup>
              {targetCandidates.map(({ offer, idx }) => (
                <FormControlLabel
                  key={idx}
                  control={
                    <Checkbox
                      checked={targetIndexes.includes(idx)}
                      onChange={() => toggleTarget(idx)}
                    />
                  }
                  label={getOfferLabel(offer, idx)}
                />
              ))}
            </FormGroup>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleApply} disabled={isSaveDisabled}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageOfferFieldsDialog;
